import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import {
  ArrowRight, Brain, Target, Users, FileText, Calculator,
  Shield, Zap, BarChart3, Award, Mic, TrendingUp, Lock
} from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
const stagger = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function FeaturesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="features-page">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] block mb-3" style={{ color: '#0D9488' }}>
              Platform Features
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-5" style={{ color: '#0D1E3A' }}>
              Built for career<br /><span style={{ color: '#0D9488' }}>clarity</span>, not guesswork
            </h1>
            <p className="text-base lg:text-lg max-w-2xl mx-auto mb-8" style={{ color: '#64748b' }}>
              Every feature is designed to give you deeper self-awareness, sharper career strategy,
              and the confidence to make your next move.
            </p>
            <Button onClick={() => navigate('/register')}
              className="h-12 px-8 text-sm font-bold rounded-xl"
              style={{ background: '#0D9488', color: '#fff' }}
              data-testid="features-cta-btn">
              Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 lg:py-28" style={{ background: '#f8fafc' }}>
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: '#0D1E3A' }}>
              Core Intelligence Suite
            </h2>
          </motion.div>

          {/* Feature blocks - alternating layout */}
          {[
            {
              icon: Brain, color: '#0D9488',
              title: 'Career Intelligence Engine',
              desc: 'A multi-phase assessment system that decodes your decision patterns, values, and hidden strengths. Not a personality quiz. A structured intelligence framework built by career scientists.',
              details: ['16-question discovery sequence', '7 assessment batteries (OCEAN, EQ, DISC, Strengths, Hogan, KFLA, Stallers)', '12-dimension grading engine', 'Progressive confidence scoring'],
              image: '/mockups/assessment-mock.jpg',
            },
            {
              icon: Users, color: '#F5841F',
              title: 'Expert Advisory Panel',
              desc: 'Your results are reviewed by an assembled panel of career experts, each specialized in your industry, strengths, and goals. They evolve as you do.',
              details: ['10-member panel per session', 'Industry-specific expertise', 'Evolving recommendations', 'Stress-test your career plan'],
              image: '/mockups/dashboard-mock.jpg',
            },
            {
              icon: FileText, color: '#16A34A',
              title: 'Resume Studio & ATS Scoring',
              desc: 'Build resumes that pass ATS filters. Upload your existing resume, get an instant score, and optimize every line for maximum impact.',
              details: ['4 professional templates', 'Instant ATS compatibility score', 'PDF & DOCX export', 'Import and enhance existing resumes'],
              image: '/mockups/resume-mock.jpg',
            },
          ].map((feature, i) => (
            <motion.div key={feature.title} {...stagger} transition={{ delay: 0.1 }}
              className={`flex flex-col ${i % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center mb-20 last:mb-0`}>
              <div className="flex-1">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${feature.color}10` }}>
                  <feature.icon className="w-6 h-6" style={{ color: feature.color }} />
                </div>
                <h3 className="text-2xl font-extrabold mb-3" style={{ color: '#0D1E3A' }}>{feature.title}</h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: '#64748b' }}>{feature.desc}</p>
                <ul className="space-y-2">
                  {feature.details.map(d => (
                    <li key={d} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: `${feature.color}10` }}>
                        <Zap className="w-2.5 h-2.5" style={{ color: feature.color }} />
                      </div>
                      <span className="text-sm" style={{ color: '#475569' }}>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-xl overflow-hidden shadow-lg" style={{ border: '1px solid #e2e8f0' }}>
                  <img src={feature.image} alt={feature.title} className="w-full h-auto" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3" style={{ color: '#0D1E3A' }}>
              And much more
            </h2>
            <p className="text-sm max-w-lg mx-auto" style={{ color: '#64748b' }}>
              Tools and features designed for every stage of your career journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Target, color: '#3B82F6', title: 'Career Persona', desc: 'Discover your unique career archetype from 8 defined personas.' },
              { icon: Calculator, color: '#805AD5', title: 'Decision Calculator', desc: 'Compare two career paths with weighted ROI scoring.' },
              { icon: Mic, color: '#EC4899', title: 'Salary Negotiation Sim', desc: 'Practice negotiations with a realistic hiring manager simulation.' },
              { icon: BarChart3, color: '#F5841F', title: 'Grade Report Card', desc: 'See your scores across all 12 career dimensions.' },
              { icon: Award, color: '#0D9488', title: 'Gamification & Streaks', desc: 'Earn XP, unlock badges, and maintain career momentum.' },
              { icon: Shield, color: '#64748b', title: 'Enterprise Security', desc: 'SOC 2 compliant with AES-256 encryption.' },
              { icon: TrendingUp, color: '#16A34A', title: 'Career Progress Timeline', desc: 'Visual milestone tracking of your journey.' },
              { icon: Lock, color: '#3B82F6', title: 'Privacy Controls', desc: 'Full control over your data with GDPR compliance.' },
              { icon: Users, color: '#F5841F', title: 'Community Hub', desc: 'Connect with peers, share insights, and grow together.' },
            ].map((card, i) => (
              <motion.div key={card.title} {...stagger} transition={{ delay: 0.05 + i * 0.05 }}
                className="rounded-xl p-6" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ background: `${card.color}10` }}>
                  <card.icon className="w-5 h-5" style={{ color: card.color }} />
                </div>
                <h3 className="text-sm font-bold mb-1" style={{ color: '#0D1E3A' }}>{card.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24" style={{ background: '#0A1628' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-5" style={{ color: '#F7FAFC' }}>
              Ready to take control?
            </h2>
            <p className="text-base mb-8" style={{ color: '#9DB5DC' }}>
              Start with a free assessment and see what Kairera reveals about your career.
            </p>
            <Button onClick={() => navigate('/register')}
              className="h-13 px-8 text-base font-bold rounded-xl"
              style={{ background: '#0D9488', color: '#fff', height: '52px' }}>
              Start Free <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
